import Cookies from "js-cookie";

import { readConsent } from "@/lib/hooks/use-marketing-consent";

/**
 * First-touch acquisition (ad source / campaign) for the visitor. Stored in a
 * first-party cookie ONLY while marketing consent is granted, and sent with the
 * checkout / free-join request so the server can save it on the member. The
 * server re-validates everything; this is just transport.
 */
export type Attribution = {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  term?: string;
  fbclid?: string;
  referrerHost?: string;
  landingPath?: string;
  capturedAt?: string;
};

const ATTRIBUTION_COOKIE = "ts_attribution";
const ATTRIBUTION_MAX_AGE_DAYS = 90;
const MAX_VALUE_LENGTH = 200;

const UTM_PARAMS = {
  source: "utm_source",
  medium: "utm_medium",
  campaign: "utm_campaign",
  content: "utm_content",
  term: "utm_term",
} as const;

const clean = (value: string | null | undefined) =>
  value?.trim().slice(0, MAX_VALUE_LENGTH) || undefined;

// An unparseable or same-site referrer is simply "no referrer signal".
const getExternalReferrerHost = () => {
  if (!document.referrer) return undefined;
  let host: string;
  try {
    host = new URL(document.referrer).hostname;
  } catch {
    return undefined;
  }
  return host === window.location.hostname ? undefined : clean(host);
};

const readStoredAttribution = (): Attribution | null => {
  const raw = Cookies.get(ATTRIBUTION_COOKIE);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Attribution;
  } catch (error) {
    console.warn("Ignoring unreadable attribution cookie", error);
    return null;
  }
};

/**
 * Record where the visitor came from, once (first touch wins). No-op without
 * consent, when a value is already stored, or when the visit carries no signal
 * (direct traffic) — so a later ad click can still be captured.
 */
export const captureAttribution = () => {
  if (typeof window === "undefined" || !readConsent().value) return;
  if (readStoredAttribution()) return;

  const params = new URLSearchParams(window.location.search);
  const attribution: Attribution = {
    referrerHost: getExternalReferrerHost(),
  };
  for (const [key, param] of Object.entries(UTM_PARAMS)) {
    attribution[key as keyof typeof UTM_PARAMS] = clean(params.get(param));
  }

  // Meta only adds `fbclid` on its own; without UTMs that still tells us the
  // visit came from a Facebook/Instagram ad.
  attribution.fbclid = clean(params.get("fbclid"));
  if (!attribution.source && attribution.fbclid) {
    attribution.source = "facebook";
  }

  const hasSignal = Object.values(attribution).some(Boolean);
  if (!hasSignal) return;

  attribution.landingPath = clean(window.location.pathname);
  attribution.capturedAt = new Date().toISOString();

  Cookies.set(ATTRIBUTION_COOKIE, JSON.stringify(attribution), {
    expires: ATTRIBUTION_MAX_AGE_DAYS,
    sameSite: "Lax",
    path: "/",
  });
};

/** The stored attribution, or null when there is none or consent was withdrawn. */
export const getAttribution = (): Attribution | null => {
  if (typeof window === "undefined" || !readConsent().value) return null;
  return readStoredAttribution();
};
